/**
 * CriticalSystemsCheck — 6-checkbox mechanicals matrix + warn-banner.
 *
 * When 3+ of Foundation / Roof / HVAC / HWH / Plumbing / Electrical are
 * flagged, the endpoint returns `bump_suggested: true` and the operator
 * sees a soft banner suggesting a rehab-bracket bump. Toggling any box
 * roundtrips to /v3/properties/:id/critical-systems and receives fresh
 * metrics back so downstream MAO tiles refresh in one shot.
 *
 * Two modes:
 *   compact=true  — used inside Call Mode's live analyzer (dense list)
 *   compact=false — used on the Deal Card's strategy tab (fuller UI)
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertTriangle, HardHat, Home, Wind, Droplets, Wrench, Zap,
} from "lucide-react";

const SYSTEMS = [
  { key: "foundation",       label: "Foundation", icon: HardHat  },
  { key: "roof",             label: "Roof",       icon: Home     },
  { key: "hvac",             label: "HVAC",       icon: Wind     },
  { key: "hot_water_heater", label: "Water heater", icon: Droplets },
  { key: "plumbing",         label: "Plumbing",   icon: Wrench   },
  { key: "electrical",       label: "Electrical", icon: Zap      },
];

const BRACKET_LABEL = {
  cosmetic:  "Cosmetic",
  standard:  "Standard",
  full:      "Full",
  full_plus: "Full + Systems",
  gut:       "Gut",
};

export default function CriticalSystemsCheck({
  propertyId, initial, compact = false, onChange,
}) {
  const [flags, setFlags]     = useState(initial?.flags || {});
  const [snapshot, setSnap]   = useState(initial || null);
  const [saving, setSaving]   = useState(null);   // key currently saving
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (initial) {
      setFlags(initial.flags || {});
      setSnap(initial);
    }
  }, [initial]);

  const toggle = async (key) => {
    if (!propertyId) return;
    const next = { ...flags, [key]: !flags[key] };
    setFlags(next);
    setSaving(key);
    try {
      const { data } = await api.patch(
        `/v3/properties/${propertyId}/critical-systems`,
        { critical_systems: { [key]: !!next[key] } },
      );
      setSnap(data.metrics?.critical_systems);
      onChange?.(data.metrics);
    } catch {
      // Roll back the optimistic flip on failure.
      setFlags(flags);
      toast.error("Couldn't save that toggle — try again");
    } finally { setSaving(null); }
  };

  const applyBump = async () => {
    if (!propertyId || !snapshot?.suggested_bracket) return;
    setApplied(true);
    try {
      const { data } = await api.patch(
        `/v3/properties/${propertyId}/critical-systems`,
        { rehab_level: snapshot.suggested_bracket },
      );
      setSnap(data.metrics?.critical_systems);
      onChange?.(data.metrics);
      toast.success(`Rehab bracket set to ${BRACKET_LABEL[snapshot.suggested_bracket]}`);
    } catch {
      toast.error("Couldn't apply the bump — try again");
    } finally { setTimeout(() => setApplied(false), 800); }
  };

  const flaggedCount = snapshot?.flagged_count ?? Object.values(flags).filter(Boolean).length;
  const bump = snapshot?.bump_suggested;

  return (
    <div data-testid="critical-systems-check">
      {!compact && (
        <div className="mb-2 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">
              Critical Systems Check
            </div>
            <div className="text-[12px] text-slate-500">
              Flag anything broken. 3+ flags triggers a rehab warning.
            </div>
          </div>
          <div className="text-[11px] font-bold text-slate-700 tabular-nums">
            {flaggedCount} / {SYSTEMS.length} flagged
          </div>
        </div>
      )}

      {compact && (
        <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">
          Critical Systems · {flaggedCount}/{SYSTEMS.length}
        </div>
      )}

      <div className={compact ? "grid grid-cols-2 gap-1.5" : "grid grid-cols-2 sm:grid-cols-3 gap-2"}>
        {SYSTEMS.map(({ key, label, icon: Icon }) => {
          const on = !!flags[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              disabled={saving === key}
              className={`rounded-lg border px-2.5 py-2 text-left inline-flex items-center gap-2 transition-all ${
                on
                  ? "border-rose-300 bg-rose-50 text-rose-800"
                  : "border-slate-200 bg-white hover:border-slate-300 text-slate-600"
              } ${saving === key ? "opacity-60" : ""}`}
              data-testid={`cs-${key}`}
              aria-pressed={on}
            >
              <Icon className={`w-3.5 h-3.5 ${on ? "text-rose-600" : "text-slate-400"}`} />
              <span className={`${compact ? "text-[11.5px]" : "text-[12.5px]"} font-semibold truncate`}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Warn banner — 3+ flags triggers */}
      {bump && (
        <div
          className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2"
          data-testid="cs-warn-banner"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-bold text-amber-900">
              {flaggedCount} critical systems flagged
            </div>
            <div className="text-[11.5px] text-amber-800 leading-snug">
              Suggest bumping rehab to <b>{BRACKET_LABEL[snapshot.suggested_bracket] || "Full + Systems"}</b>
              {snapshot.current_bracket && ` (from ${BRACKET_LABEL[snapshot.current_bracket] || snapshot.current_bracket})`}.
            </div>
          </div>
          <button
            type="button"
            onClick={applyBump}
            disabled={applied}
            className="text-[11.5px] font-bold bg-amber-600 hover:bg-amber-700 text-white rounded px-2.5 py-1.5 disabled:opacity-50 shrink-0"
            data-testid="cs-apply-bump"
          >
            {applied ? "Applied" : "Apply bump"}
          </button>
        </div>
      )}
    </div>
  );
}
